/**
 * CreateOrderPage
 * Dedicated page for creating new orders
 * Integrates with order creation form/dialog
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-001
 */

import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import { motion } from 'framer-motion';
import { CreateBatchOrderDialog } from '@/components/commerce/order/CreateBatchOrderDialog';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';

/**
 * CreateOrderPage Component
 * Provides interface for creating new orders
 * Supports pre-selection of batch via URL parameters:
 * - batchId: UUID of the batch to allocate
 * - allocateBatch: true/false flag to enable batch allocation mode
 */
export default function CreateOrderPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDialogOpen, setIsDialogOpen] = useState(true);

  // Extract batch allocation parameters from URL
  const batchId = searchParams.get('batchId');
  const allocateBatch = searchParams.get('allocateBatch') === 'true';

  // Extract new customer ID from URL (passed from customer creation flow)
  const newCustomerId = searchParams.get('newCustomerId');

  // Extract existing customer ID from URL (passed from inquiry modal when customer exists)
  const customerId = searchParams.get('customerId');

  // Extract inquiry context from URL (passed from inquiry modal)
  const inquiryId = searchParams.get('inquiryId');
  const inquiryCustomerName = searchParams.get('customerName');
  const inquiryCustomerPhone = searchParams.get('customerPhone');
  const inquiryCustomerEmail = searchParams.get('customerEmail');
  const inquiryProductName = searchParams.get('productName');
  const inquiryQuantity = searchParams.get('quantity');
  const inquiryQuantityUnit = searchParams.get('quantityUnit');
  const inquiryLocation = searchParams.get('location');

  // Clear newCustomerId from URL after reading and show success message
  useEffect(() => {
    if (newCustomerId) {
      // Remove the param from URL to prevent re-selection on refresh
      searchParams.delete('newCustomerId');
      setSearchParams(searchParams, { replace: true });

      // Show success toast
      toast.success('New customer created and selected!', {
        description: 'You can now continue creating your order.',
      });
    }
  }, [newCustomerId, searchParams, setSearchParams]);

  // Show inquiry context message if coming from inquiry
  useEffect(() => {
    if (inquiryId && inquiryCustomerName) {
      if (customerId) {
        toast.success(`Creating order for ${inquiryCustomerName}`, {
          description: 'Customer automatically selected from inquiry.',
          duration: 4000,
        });
      } else {
        toast.info(`Creating order for inquiry from ${inquiryCustomerName}`, {
          description: 'Please select or create the customer to proceed.',
          duration: 5000,
        });
      }
    }
  }, [inquiryId, inquiryCustomerName, customerId]);

  // Determine which customer ID to use (priority: newCustomerId > customerId)
  const effectiveCustomerId = newCustomerId || customerId;

  /**
   * Handle back navigation
   */
  function handleBack() {
    navigate('/sales/orders');
  }

  /**
   * Handle order created success
   */
  async function handleOrderSuccess(orderData?: any) {
    // Navigate back to orders list after successful creation
    navigate('/sales/orders');
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 py-6 px-4 sm:px-6 lg:px-8">
      <div className="container mx-auto max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-6 flex items-center gap-4"
        >
          <Button
            onClick={handleBack}
            variant="outline"
            size="icon"
            className="rounded-full border-slate-300 bg-white hover:bg-slate-100"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-4xl font-bold text-slate-900">Create New Order</h1>
            <p className="text-sm text-slate-500 mt-1">
              Create a new purchase order from an active contract
            </p>
          </div>
        </motion.div>

        {/* Inquiry Context Card - Show if creating order from inquiry */}
        {inquiryId && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-6"
          >
            <Card className="border-blue-200 bg-blue-50/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-blue-900">
                  Creating Order from Customer Inquiry
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="font-medium text-blue-800">Customer:</span>{' '}
                    <span className="text-blue-700">{inquiryCustomerName}</span>
                  </div>
                  <div>
                    <span className="font-medium text-blue-800">Phone:</span>{' '}
                    <span className="text-blue-700">{inquiryCustomerPhone}</span>
                  </div>
                  {inquiryCustomerEmail && (
                    <div>
                      <span className="font-medium text-blue-800">Email:</span>{' '}
                      <span className="text-blue-700">{inquiryCustomerEmail}</span>
                    </div>
                  )}
                  {inquiryProductName && (
                    <div>
                      <span className="font-medium text-blue-800">Product:</span>{' '}
                      <span className="text-blue-700">{inquiryProductName}</span>
                    </div>
                  )}
                  {inquiryQuantity && (
                    <div>
                      <span className="font-medium text-blue-800">Quantity:</span>{' '}
                      <span className="text-blue-700">
                        {inquiryQuantity} {inquiryQuantityUnit || 'units'}
                      </span>
                    </div>
                  )}
                  {inquiryLocation && (
                    <div>
                      <span className="font-medium text-blue-800">Location:</span>{' '}
                      <span className="text-blue-700">{inquiryLocation}</span>
                    </div>
                  )}
                </div>
                <Alert className="mt-3 bg-white border-blue-200">
                  <AlertTriangle className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800">
                    Please select the customer from the dropdown or create a new customer entry below.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <CreateBatchOrderDialog
            open={isDialogOpen}
            onOpenChange={(open) => {
              setIsDialogOpen(open);
              // If dialog closes, navigate back
              if (!open) {
                navigate('/sales/orders');
              }
            }}
            onOrderCreated={handleOrderSuccess}
            defaultCustomerId={effectiveCustomerId ? parseInt(effectiveCustomerId) : undefined}
          />
        </motion.div>
      </div>
    </div>
  );
}
